(() => {
  if (customElements.get('component-cart-target')) return;

  // Every cart-target reacting to the same cart event swaps inside ONE coordinated view
  // transition. `document.startViewTransition` is a singleton — if each target started
  // its own, every call would skip the previous one, so only the last animated and the
  // rest snapped harshly. Each target enqueues its swap; a microtask then runs the
  // batch together: off-screen swaps happen outside the transition, on-screen ones
  // animate as one.
  let batch = [];
  let scheduled = false;

  const flush = () => {
    scheduled = false;

    const renders = batch;
    batch = [];

    // Off-screen targets (e.g. the notification body behind a closed dialog) swap
    // outside the transition — animating them is a no-op overlay that flashes over the
    // modal as it opens — and signal completion immediately so their consumers (the
    // modal opening on `cart-target:rendered`) aren't held back by the animation.
    renders
      .filter((render) => !render.visible)
      .forEach((render) => {
        render.update();
        render.done();
      });

    const visible = renders.filter((render) => render.visible);
    if (!visible.length) return;

    const update = () => visible.forEach((render) => render.update());
    const done = () => visible.forEach((render) => render.done());

    if (document.startViewTransition) {
      document.startViewTransition(update).updateCallbackDone.then(done);
    } else {
      update();
      done();
    }
  };

  const enqueue = (render) => {
    batch.push(render);

    if (scheduled) return;
    scheduled = true;
    queueMicrotask(flush);
  };

  class CartTarget extends HTMLElement {
    connectedCallback() {
      this.section = this.dataset.section;

      document.addEventListener('cart:collect-sections', this.onCollect);
      document.addEventListener('cart:added', this.onCartChange);
      document.addEventListener('cart:updated', this.onCartChange);
    }

    disconnectedCallback() {
      document.removeEventListener('cart:collect-sections', this.onCollect);
      document.removeEventListener('cart:added', this.onCartChange);
      document.removeEventListener('cart:updated', this.onCartChange);
    }

    onCollect = ({ detail }) => {
      if (this.section) detail.sections.add(this.section);
    };

    onCartChange = ({ detail }) => {
      const html = detail?.sections?.[this.section];
      if (!html) return;

      this.render(html, detail.item, detail.trigger);
    };

    render(html, item, trigger) {
      const doc = new DOMParser().parseFromString(html, 'text/html');

      const selector = this.dataset.selector?.replace(/\{(\w+)\}/g, (_, field) => item?.[field] ?? '');
      const scoped = selector && doc.querySelector(selector);

      const update = () => {
        if (scoped) {
          this.replaceChildren(scoped);
        } else {
          const wrapper = doc.querySelector('[id^="shopify-section-"]');
          this.replaceChildren(...(wrapper ? wrapper.childNodes : doc.body.childNodes));
        }
      };

      // Fire once the fresh content is in the DOM, so consumers (e.g. the modal
      // opening on this) see it. `trigger` rides along so the modal can return
      // focus to the add-to-cart button on close.
      const done = () =>
        this.dispatchEvent(
          new CustomEvent('cart-target:rendered', {
            bubbles: true,
            detail: { section: this.section, trigger },
          }),
        );

      // Hand the swap to the shared batch so concurrent target updates share one view
      // transition. `visible` decides whether this swap animates: off-screen content
      // (e.g. the notification behind a closed dialog) swaps instantly — see flush().
      enqueue({ update, done, visible: !!this.checkVisibility?.() });
    }
  }

  customElements.define('component-cart-target', CartTarget);
})();
